<template>
  <div v-if="show" class="sc-wrap">

    <!-- ── PHASE 1: draw signature ───────────────────────── -->
    <template v-if="phase === 'sign'">
      <p class="sc-instruction">{{ translation.instruction }}</p>

      <!-- Image with dashed zone showing where the sig will appear -->
      <div class="sc-img-container">
        <img :src="imageUrl" class="sc-img" />
        <div class="sc-zone-hint" :style="positionStyle({ dashed: true })">
          <span class="sc-zone-label">{{ translation.signHere }}</span>
        </div>
      </div>

      <!-- Signature pad -->
      <div class="sc-pad-section">
        <p class="sc-pad-label">{{ translation.drawBelow }}</p>
        <div class="sc-canvas-wrap">
          <canvas
            ref="sigCanvas"
            class="sc-canvas"
            @pointerdown.prevent="onDown"
            @pointermove.prevent="onMove"
            @pointerup="onUp"
            @pointerleave="onUp"
            @pointercancel="onUp"
          />
          <span v-if="!hasDrawn" class="sc-canvas-placeholder">{{ translation.signPlaceholder }}</span>
        </div>
        <div class="sc-pad-btns">
          <button class="sc-btn-clear" @click="clearSig">{{ translation.clear }}</button>
          <button class="sc-btn-ok" :disabled="!hasDrawn" @click="validate">{{ translation.validate }}</button>
        </div>
      </div>
    </template>

    <!-- ── PHASE 2: result with signature overlaid ────────── -->
    <template v-else>
      <div class="sc-img-container sc-result-container">
        <img :src="imageUrl" class="sc-img" />
        <div class="sc-sig-overlay" :style="positionStyle({ dashed: false })">
          <img :src="sigDataUrl" class="sc-sig-ink" />
        </div>
      </div>

      <transition name="sc-bravo">
        <div v-if="showBravo" class="sc-bravo-overlay">
          <div class="sc-bravo-card">
            <div class="sc-bravo-icon">🖊️</div>
            <p class="sc-bravo-text">{{ translation.bravo }}</p>
          </div>
        </div>
      </transition>

      <button v-if="!showBravo" class="sc-btn-continue" @click="$emit('sc-done')">
        {{ translation.continuer }}
      </button>
    </template>

  </div>
</template>

<script>
export default {
  props: {
    pointData: { type: Object, default: null },
  },
  data() {
    return {
      phase: 'sign',
      hasDrawn: false,
      drawing: false,
      lastX: 0,
      lastY: 0,
      sigDataUrl: null,
      showBravo: false,
      translation: {
        instruction: 'Signez cette carte',
        signHere: 'Zone de signature',
        drawBelow: 'Dessinez votre signature ci-dessous',
        signPlaceholder: '✍',
        clear: 'Effacer',
        validate: 'Valider',
        bravo: 'Bien signé !',
        continuer: 'Continuer',
      },
    };
  },
  computed: {
    show() {
      return this.$parent.showSignCard === true;
    },
    imageUrl() {
      return this.pointData?.signCardImageUrl || this.pointData?.signCardImageBase64 || '';
    },
    sigHeightPct() {
      return this.pointData?.signCardSigHeightPct || 20;
    },
    sigVAlign() {
      return this.pointData?.signCardSigVAlign || 'bottom';
    },
    sigHAlign() {
      return this.pointData?.signCardSigHAlign || 'center';
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.phase = 'sign';
        this.hasDrawn = false;
        this.sigDataUrl = null;
        this.showBravo = false;
        this.$nextTick(() => this.initCanvas());
      }
    },
  },
  mounted() {
    this.translate(this.$store.getters.lang);
    this.$nextTick(() => this.initCanvas());
  },
  methods: {
    positionStyle({ dashed }) {
      const transforms = [];
      const style = {
        position: 'absolute',
        height: this.sigHeightPct + '%',
        width: '72%',
        boxSizing: 'border-box',
        borderRadius: '6px',
        overflow: 'hidden',
      };

      if (dashed) {
        style.border = '2px dashed rgba(255,255,255,0.75)';
        style.background = 'rgba(255,255,255,0.12)';
        style.display = 'flex';
        style.alignItems = 'center';
        style.justifyContent = 'center';
      } else {
        style.background = 'transparent';
        style.display = 'flex';
        style.alignItems = 'center';
        style.justifyContent = 'center';
      }

      if (this.sigVAlign === 'top')         { style.top = '4%'; }
      else if (this.sigVAlign === 'center') { style.top = '50%'; transforms.push('translateY(-50%)'); }
      else                                  { style.bottom = '4%'; }

      if (this.sigHAlign === 'left')        { style.left = '4%'; }
      else if (this.sigHAlign === 'center') { style.left = '50%'; transforms.push('translateX(-50%)'); }
      else                                  { style.right = '4%'; }

      if (transforms.length) style.transform = transforms.join(' ');
      return style;
    },

    initCanvas() {
      const canvas = this.$refs.sigCanvas;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.strokeStyle = '#111';
      ctx.lineWidth   = 2.5;
      ctx.lineCap     = 'round';
      ctx.lineJoin    = 'round';
    },

    getPos(e) {
      const canvas = this.$refs.sigCanvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },

    onDown(e) {
      const canvas = this.$refs.sigCanvas;
      canvas.setPointerCapture(e.pointerId);
      this.drawing = true;
      const { x, y } = this.getPos(e);
      this.lastX = x;
      this.lastY = y;
    },

    onMove(e) {
      if (!this.drawing) return;
      const { x, y } = this.getPos(e);
      const ctx = this.$refs.sigCanvas.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(this.lastX, this.lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      this.lastX = x;
      this.lastY = y;
      this.hasDrawn = true;
    },

    onUp() {
      this.drawing = false;
    },

    clearSig() {
      const canvas = this.$refs.sigCanvas;
      const dpr = window.devicePixelRatio || 1;
      canvas.getContext('2d').clearRect(0, 0, canvas.width / dpr * dpr, canvas.height / dpr * dpr);
      // Re-init to reset scale
      this.initCanvas();
      this.hasDrawn = false;
    },

    validate() {
      if (!this.hasDrawn) return;
      this.sigDataUrl = this.$refs.sigCanvas.toDataURL('image/png');
      this.phase = 'result';

      this.$parent.errorNumber += 3;
      this.$parent.showStarUp('3+');
      this.$parent.showCorrectAnswerIcon = true;
      localStorage.setItem('errorNumber', this.$parent.errorNumber);
      setTimeout(() => { this.$parent.showCorrectAnswerIcon = false; }, 800);

      // Show bravo briefly, then reveal the continue button (no auto-advance)
      this.$nextTick(() => {
        setTimeout(() => {
          this.showBravo = true;
          setTimeout(() => { this.showBravo = false; }, 2000);
        }, 300);
      });
    },

    translate(lang) {
      const map = {
        fr: {
          instruction: 'Signez cette carte',
          signHere: 'Zone de signature',
          drawBelow: 'Dessinez votre signature ci-dessous',
          signPlaceholder: '✍',
          clear: 'Effacer',
          validate: 'Valider',
          bravo: 'Bien signé !',
          continuer: 'Continuer',
        },
        he: {
          instruction: 'חתמו על הכרטיס',
          signHere: 'אזור חתימה',
          drawBelow: 'צייר את החתימה שלך למטה',
          signPlaceholder: '✍',
          clear: 'נקה',
          validate: 'אשר',
          bravo: '!חתמת בהצלחה',
          continuer: 'המשך',
        },
        en: {
          instruction: 'Sign this card',
          signHere: 'Signature zone',
          drawBelow: 'Draw your signature below',
          signPlaceholder: '✍',
          clear: 'Clear',
          validate: 'Confirm',
          bravo: 'Well signed!',
          continuer: 'Continue',
        },
        de: {
          instruction: 'Unterschreiben Sie diese Karte',
          signHere: 'Signaturfeld',
          drawBelow: 'Zeichnen Sie Ihre Unterschrift unten',
          signPlaceholder: '✍',
          clear: 'Löschen',
          validate: 'Bestätigen',
          bravo: 'Unterschrift akzeptiert!',
          continuer: 'Weiter',
        },
        ar: {
          instruction: 'وقّع على هذه البطاقة',
          signHere: 'منطقة التوقيع',
          drawBelow: 'ارسم توقيعك أدناه',
          signPlaceholder: '✍',
          clear: 'امسح',
          validate: 'تأكيد',
          bravo: '!تم التوقيع بنجاح',
          continuer: 'متابعة',
        },
      };
      this.translation = map[lang] || map.fr;
    },
  },
};
</script>

<style scoped>
.sc-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 16px 12px 80px;
}

/* ── Instruction ───────────────────────────────────────── */
.sc-instruction {
  font-family: 'VarelaRound', sans-serif;
  font-size: 20px;
  font-weight: bold;
  color: white;
  text-align: center;
  margin: 0;
}

/* ── Image container ───────────────────────────────────── */
.sc-img-container {
  position: relative;
  width: 100%;
  max-width: 560px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 28px rgba(0,0,0,0.45);
}

.sc-img {
  width: 100%;
  display: block;
}

/* ── Zone hint (phase 1) ───────────────────────────────── */
.sc-zone-label {
  font-family: 'VarelaRound', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ── Signature overlay (phase 2) ───────────────────────── */
.sc-sig-ink {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* ── Signature pad ─────────────────────────────────────── */
.sc-pad-section {
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sc-pad-label {
  font-family: 'VarelaRound', sans-serif;
  font-size: 15px;
  color: rgba(255,255,255,0.8);
  margin: 0;
  text-align: center;
}

.sc-canvas-wrap {
  position: relative;
  width: 100%;
  height: 160px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.35);
  overflow: hidden;
  cursor: crosshair;
}

.sc-canvas {
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
}

.sc-canvas-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: rgba(0,0,0,0.12);
  pointer-events: none;
  user-select: none;
}

/* ── Pad buttons ───────────────────────────────────────── */
.sc-pad-btns {
  display: flex;
  gap: 12px;
}

.sc-btn-clear {
  flex: 1;
  font-family: 'VarelaRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  padding: 14px;
  border-radius: 50px;
  border: 2.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: white;
  cursor: pointer;
  transition: background 0.15s;
}

.sc-btn-clear:hover {
  background: rgba(255,255,255,0.1);
}

.sc-btn-ok {
  flex: 2;
  font-family: 'VarelaRound', sans-serif;
  font-size: 17px;
  font-weight: 700;
  padding: 14px;
  border-radius: 50px;
  border: none;
  background: #1976d2;
  box-shadow: 0 5px 0 #0d47a1, 0 8px 14px rgba(0,0,0,0.3);
  color: white;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.08s, box-shadow 0.08s;
}

.sc-btn-ok:active:not(:disabled) {
  transform: translateY(3px);
  box-shadow: 0 2px 0 #0d47a1;
}

.sc-btn-ok:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ── Bravo overlay ─────────────────────────────────────── */
.sc-bravo-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.sc-bravo-card {
  background: linear-gradient(135deg, #1565c0, #1976d2);
  border: 2px solid rgba(255,255,255,0.2);
  border-radius: 28px;
  padding: 40px 48px;
  text-align: center;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
}

.sc-bravo-icon {
  font-size: 56px;
  line-height: 1;
  margin-bottom: 14px;
}

.sc-bravo-text {
  font-family: 'VarelaRound', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: white;
  margin: 0;
}

.sc-bravo-enter-active { animation: sc-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes sc-pop {
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1); }
}

.sc-result-container { cursor: default; }

/* ── Continue button (phase 2) ─────────────────────────── */
.sc-btn-continue {
  width: 100%;
  max-width: 560px;
  font-family: 'VarelaRound', sans-serif;
  font-size: 18px;
  font-weight: 700;
  padding: 16px;
  border-radius: 50px;
  border: none;
  background: #1976d2;
  box-shadow: 0 5px 0 #0d47a1, 0 8px 14px rgba(0,0,0,0.3);
  color: white;
  cursor: pointer;
  transition: transform 0.08s, box-shadow 0.08s;
  margin-top: 4px;
}

.sc-btn-continue:active {
  transform: translateY(3px);
  box-shadow: 0 2px 0 #0d47a1;
}
</style>
